import { create } from 'zustand';
import {
  PDA1040_R01HeaderDto,
  PDA1040_R01DetailDto,
  PDA1040_R01ItemDto,
  PDA1040_R01SearchCondition,
} from 'src/containers/PDA/PDA1040_R01/PDA1040_R01Dto';

/**
 * 검수 항목 (로컬 임시 저장)
 */
export interface CheckedItem {
  id: string; // 고유 ID (timestamp 등)
  itemCd: string;
  itemNm: string;
  barcode: string;
  checkQty: number;
  warehouseCd: string;
  warehouseNm: string;
  remark?: string;
}

interface PDA1040_R01State {
  // 로딩 상태
  loading: boolean;
  setLoading: (loading: boolean) => void;

  // 검색 조건
  searchCondition: PDA1040_R01SearchCondition;
  setSearchCondition: (condition: PDA1040_R01SearchCondition) => void;

  // 발주 목록 (좌측 그리드)
  orderList: PDA1040_R01HeaderDto[];
  setOrderList: (list: PDA1040_R01HeaderDto[]) => void;

  // 선택된 발주
  selectedOrder: PDA1040_R01HeaderDto | null;
  setSelectedOrder: (order: PDA1040_R01HeaderDto | null) => void;

  // 발주 상세 목록 (하단 그리드)
  detailList: PDA1040_R01DetailDto[];
  setDetailList: (list: PDA1040_R01DetailDto[]) => void;

  // 스캔된 품목 정보
  scannedItem: PDA1040_R01ItemDto | null;
  setScannedItem: (item: PDA1040_R01ItemDto | null) => void;

  // 바코드 입력값
  barcode: string;
  setBarcode: (barcode: string) => void;

  // 입고수량
  transportQty: number;
  setTransportQty: (qty: number) => void;

  // 창고코드
  warehouseCd: string;
  setWarehouseCd: (cd: string) => void;

  // 창고명
  warehouseNm: string;
  setWarehouseNm: (nm: string) => void;

  // 비고
  remark: string;
  setRemark: (remark: string) => void;

  // 검수 목록 (로컬 임시 저장)
  checkedList: CheckedItem[];
  setCheckedList: (list: CheckedItem[]) => void;
  addCheckedItem: (item: CheckedItem) => void;
  removeCheckedItem: (id: string) => void;
  clearCheckedList: () => void;
  updateCheckedItemQty: (id: string, qty: number) => void;

  // 초기화
  reset: () => void;
  resetScanForm: () => void; // 스캔 폼만 초기화
}

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const today = new Date();
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

const initialState = {
  loading: false,
  searchCondition: {
    startDate: formatLocalDate(firstDay),  //   "2026-02-01"
    endDate: formatLocalDate(today),       //   "2026-02-02"
  },
  orderList: [],
  selectedOrder: null,
  detailList: [],
  scannedItem: null,
  barcode: '',
  transportQty: 0,
  warehouseCd: '',
  warehouseNm: '',
  remark: '',
  checkedList: [],
};

const usePDA1040_R01Store = create<PDA1040_R01State>((set) => ({
  ...initialState,

  setLoading: (loading) => set({ loading }),

  setSearchCondition: (condition) => set({ searchCondition: condition }),

  setOrderList: (list) => set({ orderList: list }),

  setSelectedOrder: (order) => set({ selectedOrder: order }),

  setDetailList: (list) => set({ detailList: list }),

  setScannedItem: (item) => set({ scannedItem: item }),

  setBarcode: (barcode) => set({ barcode }),

  setTransportQty: (qty) => set({ transportQty: qty }),

  setWarehouseCd: (cd) => set({ warehouseCd: cd }),

  setWarehouseNm: (nm) => set({ warehouseNm: nm }),

  setRemark: (remark) => set({ remark }),

  setCheckedList: (list) => set({ checkedList: list }),

  addCheckedItem: (item) =>
    set((state) => ({
      checkedList: [...state.checkedList, item],
    })),

  removeCheckedItem: (id) =>
    set((state) => ({
      checkedList: state.checkedList.filter((item) => item.id !== id),
    })),

  clearCheckedList: () => set({ checkedList: [] }),

  updateCheckedItemQty: (id, qty) =>
    set((state) => ({
      checkedList: state.checkedList.map((item) =>
        item.id === id ? { ...item, checkQty: qty } : item
      ),
    })),

  
  reset: () => set(initialState),

  resetScanForm: () =>
    set({
      scannedItem: null,
      barcode: '',
      transportQty: 0,
      remark: '',
      // warehouseCd, warehouseNm은 유지 (검수 후에도 같은 창고 사용)
    }),
}));

export default usePDA1040_R01Store;
